import CodeBlock from '../CodeBlock';

function FnSignature({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-left font-mono text-sm bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-2.5 mb-3 text-gray-300 overflow-x-auto">
      {children}
    </div>
  );
}

export default function DocsBacktestModule() {
  return (
    <section data-docs-section="backtest" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#backtest" className="hover:text-sky-400 transition-colors">pa.backtest</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-6">
        Vectorized single-asset backtesting engine. No row-by-row iteration — the
        entire simulation is expressed as numpy/pandas array operations. Applies
        next-bar execution (signal on bar N, trade at open of bar N+1) to avoid
        look-ahead bias.
      </p>

      {/* run() */}
      <div id="backtest-run" data-docs-section="backtest-run" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">run()</h3>
        <FnSignature>
          pa.backtest.run(prices, signal, initial_cash=100_000.0, fee_bps=0.0, slippage_bps=0.0, benchmark=None) → BacktestResult
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Run a complete backtest. Raises <code className="text-gray-300">BacktestError</code> on
          invalid inputs (empty data, missing columns, misaligned indices).
        </p>
        <div className="space-y-0 mb-3 text-left">
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50">
            <code className="text-sky-400 shrink-0">prices</code>
            <span className="text-gray-600 shrink-0">DataFrame</span>
            <span className="text-gray-400">OHLCV DataFrame with a <code className="text-gray-300">close</code> column.</span>
          </div>
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50">
            <code className="text-sky-400 shrink-0">signal</code>
            <span className="text-gray-600 shrink-0">Series</span>
            <span className="text-gray-400">Integer signal in {'{-1, 0, 1}'} aligned to same index.</span>
          </div>
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50">
            <code className="text-sky-400 shrink-0">initial_cash</code>
            <span className="text-gray-600 shrink-0">float</span>
            <span className="text-gray-400">Starting capital in dollars. Default 100,000.</span>
          </div>
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50">
            <code className="text-sky-400 shrink-0">fee_bps</code>
            <span className="text-gray-600 shrink-0">float</span>
            <span className="text-gray-400">Round-trip brokerage fee in basis points.</span>
          </div>
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50">
            <code className="text-sky-400 shrink-0">slippage_bps</code>
            <span className="text-gray-600 shrink-0">float</span>
            <span className="text-gray-400">Estimated slippage in basis points.</span>
          </div>
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50 last:border-0">
            <code className="text-sky-400 shrink-0">benchmark</code>
            <span className="text-gray-600 shrink-0">str | DataFrame | Series | None</span>
            <span className="text-gray-400">Ticker string, DataFrame with close, or pre-computed returns.</span>
          </div>
        </div>
        <CodeBlock
          code={`result = pa.backtest.run(
    prices,
    signal,
    initial_cash=100_000,
    fee_bps=5.0,
    slippage_bps=2.0,
    benchmark="SPY",
)

print(result.summary())`}
        />
      </div>

      {/* BacktestResult */}
      <div id="backtest-result" data-docs-section="backtest-result" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">BacktestResult</h3>
        <p className="text-gray-400 text-sm mb-3">
          Frozen dataclass returned by <code className="text-gray-300">run()</code>. Contains
          the complete backtest output.
        </p>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Attribute</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Type</th>
                <th className="py-2 text-gray-400 font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">equity_curve</td>
                <td className="py-2 pr-4 text-gray-500">Series</td>
                <td className="py-2 text-gray-400">Dollar equity indexed by date</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">returns</td>
                <td className="py-2 pr-4 text-gray-500">Series</td>
                <td className="py-2 text-gray-400">Daily simple returns of the strategy</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">positions</td>
                <td className="py-2 pr-4 text-gray-500">Series</td>
                <td className="py-2 text-gray-400">Position at each bar (-1, 0, or 1)</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">trades</td>
                <td className="py-2 pr-4 text-gray-500">DataFrame</td>
                <td className="py-2 text-gray-400">One row per position change (date, direction, price, cost)</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">drawdowns</td>
                <td className="py-2 pr-4 text-gray-500">Series</td>
                <td className="py-2 text-gray-400">Peak-to-trough drawdown fraction (non-positive)</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">benchmark_equity</td>
                <td className="py-2 pr-4 text-gray-500">Series | None</td>
                <td className="py-2 text-gray-400">Benchmark equity curve if supplied</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">benchmark_returns</td>
                <td className="py-2 pr-4 text-gray-500">Series | None</td>
                <td className="py-2 text-gray-400">Benchmark daily returns if supplied</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">metrics</td>
                <td className="py-2 pr-4 text-gray-500">dict[str, float]</td>
                <td className="py-2 text-gray-400">Computed metrics (sharpe, sortino, max_drawdown, ...)</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">initial_cash</td>
                <td className="py-2 pr-4 text-gray-500">float</td>
                <td className="py-2 text-gray-400">Starting capital</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-sky-400">cost_model</td>
                <td className="py-2 pr-4 text-gray-500">CostModel</td>
                <td className="py-2 text-gray-400">Fee/slippage assumptions</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h4 className="text-base font-semibold text-white mt-6 mb-2">Methods</h4>
        <div className="space-y-2 mb-4">
          <div className="text-sm">
            <code className="text-sky-400">result.summary()</code>
            <span className="text-gray-500"> → str</span>
            <span className="text-gray-400 ml-2">— Formatted text summary of key metrics</span>
          </div>
          <div className="text-sm">
            <code className="text-sky-400">result.to_dict()</code>
            <span className="text-gray-500"> → dict</span>
            <span className="text-gray-400 ml-2">— Plain dict suitable for JSON consumers</span>
          </div>
          <div className="text-sm">
            <code className="text-sky-400">result.to_json()</code>
            <span className="text-gray-500"> → str</span>
            <span className="text-gray-400 ml-2">— JSON string with ISO-formatted date keys</span>
          </div>
        </div>
      </div>

      {/* CostModel */}
      <div id="backtest-costs" data-docs-section="backtest-costs" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">CostModel</h3>
        <FnSignature>
          CostModel(fee_bps=0.0, slippage_bps=0.0)
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Frozen dataclass for transaction cost specification. Basis points
          are combined into a single rate: <code className="text-gray-300">(fee_bps + slippage_bps) / 10_000</code>.
        </p>
        <CodeBlock
          code={`from propalgos.backtest.costs import CostModel

cost = CostModel(fee_bps=5.0, slippage_bps=2.0)
# Total cost per trade: 7 bps = 0.07%`}
        />
      </div>

      {/* Metrics table */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-white mb-3">Metrics Dictionary</h3>
        <p className="text-gray-400 text-sm mb-3">
          The <code className="text-gray-300">result.metrics</code> dict contains these keys:
        </p>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Key</th>
                <th className="py-2 text-gray-400 font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {[
                ['sharpe', 'Annualized Sharpe ratio'],
                ['sortino', 'Annualized Sortino ratio'],
                ['calmar', 'Calmar ratio (CAGR / |max DD|)'],
                ['total_return', 'Total cumulative return'],
                ['annualized_return', 'CAGR'],
                ['annualized_volatility', 'Annualized standard deviation'],
                ['max_drawdown', 'Maximum peak-to-trough drawdown (negative)'],
                ['win_rate', 'Fraction of positive-return periods'],
                ['profit_factor', 'Gross profits / gross losses'],
                ['num_trades', 'Total number of trades'],
                ['avg_trade_return', 'Average return on trade days'],
                ['beta', 'Beta vs benchmark (only if benchmark supplied)'],
                ['alpha', 'Alpha vs benchmark (only if benchmark supplied)'],
              ].map(([key, desc]) => (
                <tr key={key} className="border-b border-gray-800/50 last:border-0">
                  <td className="py-1.5 pr-4 font-mono text-sky-400">{key}</td>
                  <td className="py-1.5 text-gray-400">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
